import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { Container, Reveal, SectionHead } from "@/components/Motion";
import { IMAGES, OM_SERVICE, SERVICES } from "@/data/content";

export default function Services() {
  return (
    <>
      <SEO
        title="IT Services | Software, Cybersecurity, Cloud, AI & O&M | EVOVLEX IT"
        description="End-to-end technology services: software development, web & mobile, cybersecurity, cloud, AI & ML, IT infrastructure, consulting, managed IT and O&M manpower services."
      />
      <PageHero
        eyebrow="Our Services"
        titleLines={[
          "Comprehensive IT Solutions",
          { node: <span>for a <span className="text-gradient-brand">Digital World</span></span> },
        ]}
        sub="EVOVLEX IT provides end-to-end technology services designed to meet the evolving requirements of businesses."
        image={IMAGES.techForward}
      />

      <section data-testid="services-technology-section" className="bg-white py-20 lg:py-28">
        <Container>
          <Reveal>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-cyan-600">
              01 — Technology Solutions
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={0.05 * (i % 4)} className="h-full">
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`services-page-card-${s.slug}`}
                  className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-cyan-500/50 hover:shadow-2xl hover:shadow-blue-600/10"
                >
                  <span className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-gradient-to-r from-blue-600 to-cyan-400 transition-transform duration-500 group-hover:scale-x-100" />
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-evo-blue transition-colors duration-300 group-hover:bg-evo-blue group-hover:text-white">
                    <s.icon className="h-6 w-6" strokeWidth={1.8} />
                  </span>
                  <h2 className="mt-5 font-display text-lg font-semibold text-slate-900">{s.title}</h2>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500">{s.short}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-evo-blue">
                    Learn More
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section data-testid="services-om-section" className="bg-slate-50 py-20 lg:py-28">
        <Container>
          <Reveal>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-evo-orange">
              02 — Operational & Workforce Solutions
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="group relative mt-8 overflow-hidden rounded-2xl border border-slate-800 bg-gradient-to-br from-navy-900 via-navy-950 to-navy-950 shadow-2xl">
              <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
              <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-orange-500/10 blur-[90px]" aria-hidden="true" />
              <div className="relative grid gap-10 p-8 lg:grid-cols-2 lg:p-14">
                <div>
                  <span className="inline-flex rounded-full bg-gradient-to-r from-orange-500 to-amber-500 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-white">
                    Operational & Workforce
                  </span>
                  <h2 className="mt-5 font-display text-2xl font-bold text-white sm:text-3xl">{OM_SERVICE.title}</h2>
                  <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-orange-300">{OM_SERVICE.tagline}</p>
                  <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">{OM_SERVICE.short}</p>
                  <Link
                    to={`/services/${OM_SERVICE.slug}`}
                    data-testid="services-page-om-cta"
                    className="mt-8 inline-flex items-center gap-2 rounded-lg bg-orange-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-orange-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-orange-600"
                  >
                    Explore O&M Services
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                <div className="grid content-center gap-3 sm:grid-cols-2">
                  {OM_SERVICE.categories.map((c) => (
                    <div
                      key={c.title}
                      data-testid={`services-om-category-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="flex items-center gap-3 rounded-lg border border-slate-800 bg-navy-900/60 px-4 py-3.5"
                    >
                      <c.icon className="h-5 w-5 shrink-0 text-orange-400" strokeWidth={1.8} />
                      <span className="text-sm font-medium text-slate-200">{c.title.replace(" Manpower", "")}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-14 flex flex-col items-start justify-between gap-6 rounded-2xl border border-slate-200 bg-white p-8 sm:flex-row sm:items-center">
              <SectionHead eyebrow="Next Step" title="Not sure which service fits?" />
              <Link
                to="/contact"
                data-testid="services-page-contact-cta"
                className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all duration-300 hover:-translate-y-0.5"
              >
                Talk to an Expert
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
